import { useState, useEffect, useRef, useContext } from 'react'
import ButtonEl from '../helpers/ctas/Button'
import { MarkupContext } from '../../providers/Markup'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import themeConfig from '../../../theme.config'
import * as HeroIcons from '@heroicons/react/24/solid'
import { dataLayerPush } from '../../helpers/thirdparty'

/**
 * MegaMenuNav - Data-sourced mega menu navigation component
 * Uses headerDataSource.megaMenuColumns structure from Umbraco
 */

const DynamicHeroIcon = (props) => {
  const { ...icons } = HeroIcons
  let TheIcon
  if (props.link?.linkIconClass) {
    TheIcon = icons[`${props.link?.linkIconClass}Icon`]
  } else if (props.icon) {
    TheIcon = icons[`${props.icon}Icon`]
  }

  return (
    <TheIcon
      className={`h-5 w-5 shrink-0 text-current ${props.iconClass ? props.iconClass : ''}`}
      role="none"
      aria-hidden="true"
    />
  )
}

const MegaMenuSection = ({
  section,
  type,
  active,
  columnHasImage,
  isExpanded,
  onExpandToggle,
  sectionId,
  onLinkKeyDown,
  onToggleKeyDown,
  category,
}) => {
  const { detectPaletteClass } = useContext(MarkupContext)
  const hasLevel3 = section.menuLinkL3?.length > 0

  return (
    <div className="mega-menu-section group relative">
      {/* Level 2 - Main section link */}
      {section.menuLinkL2 && (
        <div
          className={`
          flex items-center justify-between rounded-lg transition-colors
        `}
        >
          <a
            id={`${sectionId}-link`}
            href={section.menuLinkL2.link?.url}
            target={section.menuLinkL2.link?.target ?? '_self'}
            className="mega-menu-item-link group flex items-start gap-3 p-3 flex-1 hover:bg-(--color-button-text-hover-bg) rounded-lg"
            tabIndex={active ? 0 : -1}
            onKeyDown={onLinkKeyDown} // New Focus Routing Handler
            onClick={() => {
              dataLayerPush({
                event: 'navigation_menu_click',
                category: category,
                click_text: section.menuLinkL2.link?.name,
              })
            }}
          >
            {/* Icon */}
            {section.menuLinkL2.linkIconClass && (
              <div className="mt-0.5 shrink-0 text-(--sb-mega-menu-link-icon-color)">
                <DynamicHeroIcon
                  icon={section.menuLinkL2.linkIconClass}
                  iconClass={section.menuLinkL2.linkIconClass}
                  before={true}
                />
              </div>
            )}

            {/* Title & Description stacked vertically */}
            <div className="flex flex-col text-left">
              <span className="text-sm font-bold !text-(--sb-mega-menu-title-color) leading-6 group-hover:underline decoration-current group-hover:decoration-current underline-offset-4">
                {section.menuLinkL2.link?.name}
              </span>
              {section.menuLinkL2.linkDescription && (
                <span className="text-xs text-(--sb-mega-menu-description-color) mt-1 font-normal leading-4">
                  {section.menuLinkL2.linkDescription}
                </span>
              )}
            </div>
          </a>

          {/* Chevron controls the Level 3 pane — points right when collapsed, left when expanded */}
          {hasLevel3 && (
            <button
              id={`${sectionId}-toggle`}
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onExpandToggle()
              }}
              onKeyDown={onToggleKeyDown} // New Focus Routing Handler
              className={`group p-1 shrink-0 ml-2 text-(--sb-mega-menu-chevron-down-icon-color) cursor-pointer p-3 rounded-lg transition-colors ${isExpanded ? 'bg-(--color-button-text-hover-bg)' : 'hover:bg-(--color-button-text-hover-bg)'}`}
              aria-expanded={isExpanded}
              aria-controls={isExpanded ? `megamenu-l3-${section.menuLinkL2?.link?.name}-${type}` : undefined}
            >
              <span className="sr-only">Expand menu for {section.menuLinkL2.link?.name}</span>
              <ChevronDownIcon
                className={`h-4 w-4 scale-100 transition-transform duration-300 group-hover:scale-120 ${isExpanded ? 'rotate-90' : '-rotate-90'}`}
              />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const MegaMenuColumn = ({ column, idx, type, active, onToggle, isOpen }) => {
  const { detectPaletteClass } = useContext(MarkupContext)
  const themeHighlight = themeConfig.settings.nav?.desktop?.highlight || 'primary'
  const [activeSectionIndex, setActiveSectionIndex] = useState(null)

  const decorationColor = `--color-${themeHighlight}-500`

  const mainLink = column.mainMenuLink
  const hasChildLinks = column.sections?.some((section) => section.menuLinkL2 || section.menuLinkL3?.length > 0)

  const handleSectionExpandToggle = (sectionIndex) => {
    setActiveSectionIndex((prev) => (prev === sectionIndex ? null : sectionIndex))
  }

  useEffect(() => {
    setActiveSectionIndex(null)
  }, [isOpen])

  // ==========================================
  // WCAG FOCUS ROUTING LOGIC
  // ==========================================

  // Routes focus logically from the actual Level 2 text links
  const handleL2LinkKeyDown = (e, sIdx) => {
    const hasL3 = column.sections[sIdx].menuLinkL3?.length > 0

    // BACKWARD: If we Shift+Tab into this item, check if the item ABOVE it was open. If so, dive into its L3 list.
    if (e.key === 'Tab' && e.shiftKey && sIdx > 0) {
      const prevIdx = sIdx - 1
      if (activeSectionIndex === prevIdx && column.sections[prevIdx].menuLinkL3?.length > 0) {
        e.preventDefault()
        const lastL3Idx = column.sections[prevIdx].menuLinkL3.length - 1
        const el = document.getElementById(`l3-link-${idx}-${prevIdx}-${lastL3Idx}`)
        if (el) el.focus()
      }
    }

    // FORWARD: If this item has NO arrow button, we must manually route the focus to the next L2 link to prevent falling into the Pane 2 Trap.
    if (e.key === 'Tab' && !e.shiftKey && !hasL3) {
      e.preventDefault()
      if (sIdx < column.sections.length - 1) {
        const el = document.getElementById(`l2-${idx}-${sIdx + 1}-link`)
        if (el) el.focus()
      } else {
        const cta = document.getElementById(`cta-link-${idx}`)
        if (cta) cta.focus()
        else {
          onToggle(idx)
          const nextTop = document.getElementById(`top-link-${idx + 1}`)
          if (nextTop) nextTop.focus()
          else document.getElementById(`top-btn-${idx}`)?.focus()
        }
      }
    }
  }

  // Routes focus logically from the Level 2 Arrow Buttons
  const handleL2ToggleKeyDown = (e, sIdx) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      const isExpanded = activeSectionIndex === sIdx
      if (isExpanded) {
        // FORWARD (Open): Dive directly into the new 3rd Level list
        e.preventDefault()
        const el = document.getElementById(`l3-link-${idx}-${sIdx}-0`)
        if (el) el.focus()
      } else {
        // FORWARD (Closed): Skip to the next Level 2 item
        e.preventDefault()
        if (sIdx < column.sections.length - 1) {
          const el = document.getElementById(`l2-${idx}-${sIdx + 1}-link`)
          if (el) el.focus()
        } else {
          const cta = document.getElementById(`cta-link-${idx}`)
          if (cta) cta.focus()
          else {
            onToggle(idx)
            const nextTop = document.getElementById(`top-link-${idx + 1}`)
            if (nextTop) nextTop.focus()
            else document.getElementById(`top-btn-${idx}`)?.focus()
          }
        }
      }
    }
  }

  // Routes focus logically backwards from the Image CTA
  const handleCTAKeyDown = (e) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault()
      // BACKWARD: Did the user have a 3rd level open? Go there. If not, go to the last 2nd level item.
      if (activeSectionIndex !== null && column.sections[activeSectionIndex]?.menuLinkL3?.length > 0) {
        const lastL3Idx = column.sections[activeSectionIndex].menuLinkL3.length - 1
        const el = document.getElementById(`l3-link-${idx}-${activeSectionIndex}-${lastL3Idx}`)
        if (el) el.focus()
      } else {
        const lastL2Idx = column.sections.length - 1
        const el = document.getElementById(`l2-${idx}-${lastL2Idx}-toggle`)
        if (el) el.focus()
        else {
          const link = document.getElementById(`l2-${idx}-${lastL2Idx}-link`)
          if (link) link.focus()
        }
      }
    }
  }

  const classForOpenStatus = `${isOpen ? 'font-bold underline decoration-4 decoration-(--nav-decor)' : 'font-normal'}`

  return (
    <li
      data-level="0"
      className={`${isOpen ? 'active' : 'inactive'}`}
      onBlur={(e) => {
        if (isOpen && e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
          onToggle(idx)
        }
      }}
    >
      <div className="flex items-center megaMenuPanelAnchor h-full">
        {mainLink ? (
          <>
            <a
              id={`top-link-${idx}`}
              href={mainLink.link?.url || '#'}
              target={mainLink.link?.target ?? '_self'}
              style={{ '--nav-decor': `var(--sb-nav-decor-color)` }}
              className={detectPaletteClass(`
                h-full
                pr-1
                xl:pl-1.5
                2xl:pl-3 
                py-3 
                flex 
                items-center 
                gap-2
                text-sm
                whitespace-nowrap
                font-bold!
                leading-5
                text-(--sb-mega-menu-title-color)!
                hover:underline 
                hover:decoration-4 
                hover:decoration-(--nav-decor)
                underline-offset-8
                ${hasChildLinks ? 'cursor-pointer' : ''}
                ${classForOpenStatus}
              `)}
              aria-haspopup={hasChildLinks ? 'true' : 'false'}
              aria-controls={hasChildLinks ? `megamenu-col-${idx}` : undefined}
              aria-expanded={hasChildLinks ? (isOpen ? 'true' : 'false') : undefined}
              onClick={() => {
                dataLayerPush({
                  event: 'navigation_menu_click',
                  category: mainLink.link?.name,
                  click_text: mainLink.link?.name,
                })
              }}
            >
              <span>{mainLink.link?.name}</span>
            </a>

            {hasChildLinks && (
              <button
                type="button"
                id={`top-btn-${idx}`}
                onClick={() => onToggle(idx)}
                className={`group xl:pr-2 2xl:pr-3 p-3 h-full flex text-(--sb-mega-menu-title-color) items-center rounded-lg transition-colors ${isOpen ? 'bg-(--color-button-text-hover-bg)' : 'hover:bg-(--color-button-text-hover-bg)'}`}
              >
                <span className="sr-only">Expand menu for {mainLink.link?.name}</span>
                <ChevronDownIcon
                  className={`h-4 w-4 scale-100 transition-transform text-(--sb-mega-menu-chevron-down-icon-color) duration-300 group-hover:scale-120 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
            )}
          </>
        ) : (
          <ButtonEl
            theme="none"
            idx={idx}
            style={{ '--nav-decor': `var(--sb-nav-decor-color)` }}
            className={detectPaletteClass(`
              h-full 
              xl:px-1.5 2xl:px-3 
              py-3 
              flex 
              items-center 
              gap-2
              text-sm
              whitespace-nowrap
              !font-bold
              leading-5
              !text-(--sb-mega-menu-title-color)
              hover:underline 
              hover:decoration-4 
              hover:decoration-(--nav-decor)
              underline-offset-8
              ${classForOpenStatus}
            `)}
            ariaHasPopup="true"
            ariaControls={`megamenu-col-${idx}`}
            ariaExpanded={isOpen ? 'true' : 'false'}
            onClick={() => onToggle(idx)}
            buttonType="default"
          >
            <span>Menu</span>
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform duration-300 text-(--sb-mega-menu-chevron-down-icon-color) ${isOpen ? 'rotate-180' : ''}`}
            />
          </ButtonEl>
        )}
      </div>

      {/* Mega Menu Panel — Full Width */}
      <div
        id={`megamenu-col-${idx}`}
        className={`
          ${isOpen ? 'flex' : 'hidden'}
          absolute top-full left-0
          min-w-[900px]
          bg-white
          shadow-xl ring-1 ring-black/5
          z-50
        `}
        onMouseLeave={() => {
          if (type === 'desktop' && isOpen) {
            onToggle(idx)
          }
        }}
      >
        {/* PANE 1: Level 2 Sections — fixed width, does not shrink */}
        <div className="flex-none px-6 py-6" style={{ width: '320px' }}>
          <div className="flex flex-col space-y-1">
            {column.sections?.map((section, sIdx) => (
              <MegaMenuSection
                key={`section-${sIdx}`}
                sectionId={`l2-${idx}-${sIdx}`}
                section={section}
                type={type}
                active={isOpen}
                columnHasImage={column.megaMenuCtaImage}
                isExpanded={activeSectionIndex === sIdx}
                onExpandToggle={() => handleSectionExpandToggle(sIdx)}
                onLinkKeyDown={(e) => handleL2LinkKeyDown(e, sIdx)}
                onToggleKeyDown={(e) => handleL2ToggleKeyDown(e, sIdx)}
                category={mainLink?.link?.name}
              />
            ))}
          </div>
        </div>

        {/* PANE 2: Level 3 Links — fixed width matching L2, same bg (inherits bg-white) */}
        <div className="flex-none px-6 py-6" style={{ width: '320px' }}>
          {activeSectionIndex !== null && column.sections[activeSectionIndex]?.menuLinkL3?.length > 0 && (
            <div className="flex flex-col">
              {column.sections[activeSectionIndex].menuLinkL3.map((link, l3Idx) => {
                const isFirst = l3Idx === 0
                const isLast = l3Idx === column.sections[activeSectionIndex].menuLinkL3.length - 1

                return (
                  <a
                    id={`l3-link-${idx}-${activeSectionIndex}-${l3Idx}`}
                    key={`child-${l3Idx}`}
                    href={link.link?.url}
                    target={link.link?.target ?? '_self'}
                    className={detectPaletteClass(`
                      mega-menu-item-link group flex items-start gap-4 p-3 rounded-lg hover:bg-(--color-button-text-hover-bg) transition-colors
                    `)}
                    tabIndex={isOpen ? 0 : -1}
                    onKeyDown={(e) => {
                      // BACKWARD: Route focus BACK to the 2nd level toggle.
                      if (e.key === 'Tab' && e.shiftKey && isFirst) {
                        e.preventDefault()
                        const toggleBtn = document.getElementById(`l2-${idx}-${activeSectionIndex}-toggle`)
                        if (toggleBtn) toggleBtn.focus()
                      }

                      // FORWARD: If this is the LAST 3rd level item, route back to the NEXT 2nd level item!
                      if (e.key === 'Tab' && !e.shiftKey && isLast) {
                        const nextL2Link = document.getElementById(`l2-${idx}-${activeSectionIndex + 1}-link`)

                        if (nextL2Link) {
                          e.preventDefault()
                          nextL2Link.focus()
                        } else {
                          const ctaLink = document.getElementById(`cta-link-${idx}`)
                          if (ctaLink) {
                            e.preventDefault()
                            ctaLink.focus()
                          }
                        }
                      }
                    }}
                    onClick={() => {
                      dataLayerPush({
                        event: 'navigation_menu_click',
                        category: mainLink?.link?.name,
                        click_text: link.link?.name,
                      })
                    }}
                  >
                    {link.linkIconClass && (
                      <div className="mt-0.5 shrink-0 text-(--sb-mega-menu-link-icon-color)">
                        <DynamicHeroIcon icon={link.linkIconClass} iconClass={link.linkIconClass} before={true} />
                      </div>
                    )}
                    <div className="flex flex-col text-left">
                      <span className="text-sm font-bold !text-(--sb-mega-menu-title-color) leading-6 group-hover:underline decoration-current group-hover:decoration-current underline-offset-4">
                        {link.link?.name}
                      </span>
                      {link.linkDescription && (
                        <span className="text-xs text-(--sb-mega-menu-description-color) mt-1 font-normal leading-4">
                          {link.linkDescription}
                        </span>
                      )}
                    </div>
                  </a>
                )
              })}
            </div>
          )}
        </div>

        {/* PANE 3: Image CTA — fixed narrower width, always carries themed background.
            Content renders when a CTA image is configured; otherwise the column stays as a styled placeholder. */}
        <div className="flex-none bg-(--sb-mega-menu-img-cta-bg) px-4 py-6" style={{ width: '260px' }}>
          {column.megaMenuCtaImage &&
            (column.navigationImageLink ? (
              <a
                id={`cta-link-${idx}`}
                href={column.navigationImageLink.link?.url}
                target={column.navigationImageLink.link?.target ?? '_self'}
                className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-(--sb-focus-ring-color)"
                tabIndex={isOpen ? 0 : -1}
                onKeyDown={handleCTAKeyDown}
              >
                <img
                  src={column.megaMenuCtaImage.src}
                  alt={column.megaMenuCtaImage.altText || ''}
                  className="w-full h-auto rounded-lg"
                />
                {column.navigationImageDescription && (
                  <p className="mt-2 text-sm leading-6 text-center">{column.navigationImageDescription}</p>
                )}
              </a>
            ) : (
              <>
                <img
                  src={column.megaMenuCtaImage.src}
                  alt={column.megaMenuCtaImage.altText || ''}
                  className="w-full h-auto rounded-lg"
                />
                {column.navigationImageDescription && (
                  <p className="mt-2 text-sm leading-6 text-center">{column.navigationImageDescription}</p>
                )}
              </>
            ))}
        </div>
      </div>
    </li>
  )
}

const MegaMenuNav = (props) => {
  const [activeColumn, setActiveColumn] = useState(null)
  const { megaMenuColumns, type } = props
  const isMountedRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleToggle = (idx) => {
    setActiveColumn((prev) => {
      const newActive = prev === idx ? null : idx
      props.setMenuActive?.(newActive !== null)
      return newActive
    })
  }

  useEffect(() => {
    const handleEscape = (evt) => {
      if (evt.key === 'Escape') {
        setActiveColumn(null)
        props.setMenuActive?.(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  useEffect(() => {
    if (type === 'desktop' && activeColumn !== null && isMountedRef.current) {
      const handleClickOutside = (evt) => {
        if (!isMountedRef.current) return

        if (!evt.target.closest('[data-type="megamenu-navigation"]')) {
          setActiveColumn(null)
          props.setMenuActive?.(false)
        }
      }
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          document.addEventListener('click', handleClickOutside, true)
        }
      }, 150)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('click', handleClickOutside, true)
      }
    }
  }, [type, activeColumn])

  if (!megaMenuColumns?.length) {
    return <nav data-type="megamenu-navigation" className={`${type}-navigation relative`}></nav>
  }

  return (
    <nav data-type="megamenu-navigation" className={`hidden xl:!block ${type}-navigation h-full relative`}>
      <ul data-type="megamenu-level-0" className="flex items-center h-full">
        {megaMenuColumns.map((column, idx) => (
          <MegaMenuColumn
            key={`col-${idx}`}
            column={column}
            idx={idx}
            type={type}
            active={props.menuActive}
            isOpen={activeColumn === idx}
            onToggle={handleToggle}
          />
        ))}
      </ul>
    </nav>
  )
}

export default MegaMenuNav
